/** Avoid importing from tabs.ts (large module / cycle risk). */
const CUSTOM_PANEL_MARKER_ATTR = "data-sfe-custom-panel";

export type ToastKind = "info" | "success" | "error";

export const TOAST_CHIP_ID = "sfe-toast-chip";
const TOAST_MS = 2600;
const TOAST_ACTION_MS = 7000;

export interface ShowToastOptions {
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
}

/** Shared toast chip used by all injected tabs — never falls back to alert(). */
export function showToast(
  message: string,
  kind: ToastKind = "info",
  opts?: ShowToastOptions,
): void {
  const existing = document.getElementById(TOAST_CHIP_ID);
  existing?.remove();

  const chip = document.createElement("div");
  chip.id = TOAST_CHIP_ID;
  chip.className = `sfe-toast-chip sfe-toast-${kind}`;
  chip.setAttribute("data-sfe-injected", "true");
  chip.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  chip.setAttribute("role", "status");
  chip.setAttribute("aria-live", "polite");

  const text = document.createElement("span");
  text.className = "sfe-toast-message";
  text.textContent = message;
  chip.appendChild(text);

  const duration = opts?.durationMs ?? (opts?.actionLabel ? TOAST_ACTION_MS : TOAST_MS);
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    chip.classList.remove("sfe-toast-open");
    window.setTimeout(() => chip.remove(), 220);
  };

  if (opts?.actionLabel && opts.onAction) {
    chip.classList.add("sfe-toast-with-action");
    const action = document.createElement("button");
    action.type = "button";
    action.className = "sfe-toast-action";
    action.textContent = opts.actionLabel;
    action.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      opts.onAction?.();
      close();
    });
    chip.appendChild(action);
  }

  document.body.appendChild(chip);
  requestAnimationFrame(() => chip.classList.add("sfe-toast-open"));
  window.setTimeout(close, duration);
}

export function isToastChipNode(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.id === TOAST_CHIP_ID || Boolean(node.closest(`#${TOAST_CHIP_ID}`));
}
